import Stat from "../../../components/Stat";
import DashboardChartLawsuitCountTypeFilter from "../../../components/DashboardChartFilters/DashboardChartLawsuitCountTypeFilter";
import { ToggletipText, ToggletipTextWrapper } from "../../../style";
import useScreenBreakpoint from "../../../hooks/useScreenBreakpoint";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  hasLateActivitiesCounterAccess,
  hasLawsuitCounterComparisonStatAccess,
  hasLawsuitCounterFilterAccess,
} from "../../../selectors/powersSelectors";
import { isUp } from "../../../selectors/userInfoSelectors";
import { sendEventChartUsed } from "../../../services/dashboardAnalyticEventService";
import { dashboardChartFilterAndActionTypes } from "../../../utils/constants/dashboard/analyticDashboardEvents";
import {
  DASHBOARD_CHART_TYPES,
  LAWSUIT_COUNTER_FILTERS,
} from "../../../utils/constants/dashboard/dashboardModuleConstants";
import { lawsuitsAndCasesLocalStorageConstants } from "../../../utils/constants/localStorageConstants";
import { save as saveOnLocalStorage } from "../../../services/localStorageService";

import DashboardChartFilters from "../../../components/DashboardChartFilters";
import DashboardChartWrapper from "../../../components/DashboardChartWrapper";
import { QueryLoaderWrapper, Skeletons } from "../../../components/Loaders";
import { CounterContainer, LawsuitCounterContainer } from "./style";
import useLawsuitCounterQuery from "./useLawsuitCounterQuery";

const { LAWSUITS_AND_CASES } = lawsuitsAndCasesLocalStorageConstants;

const LawsuitCounter = () => {
  const [selectedFilter, setSelectedFilter] = useState<
    string | { queryParam: string[]; label: string }
  >(LAWSUIT_COUNTER_FILTERS.ACTIVE_LAWSUIT.queryParam);
  // @ts-ignore
  const [queryStatus, counterValues] = useLawsuitCounterQuery(selectedFilter);

  const shouldRenderLawsuitCounterFilter = useSelector(hasLawsuitCounterFilterAccess);
  const shouldRenderComparison = useSelector(hasLawsuitCounterComparisonStatAccess);
  const isLateActivitiesCounterRendered = useSelector(hasLateActivitiesCounterAccess);
  const isUpAccount = useSelector(isUp);

  const { isLargeScreen } = useScreenBreakpoint();
  const shouldRenderTooltipAtLeft = isLargeScreen && !isLateActivitiesCounterRendered;

  const setLawsuitCounterFilterAndSendMixPanelEvent = (
    filter: string | { queryParam: string[]; label: string }
  ) => {
    setSelectedFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.LAWSUIT_COUNTER,
      dashboardChartFilterAndActionTypes.LAWSUIT_COUNTER,
      filter
    );
  };

  const setLawsuitsCounterRedirect = (filterPeriod: any) => {
    const filter = isUpAccount ? LAWSUIT_COUNTER_FILTERS.ACTIVE_LAWSUIT.queryParam : selectedFilter;
    saveOnLocalStorage(LAWSUITS_AND_CASES, JSON.stringify({ filter, filterPeriod }));
    window.open("/#/main/folders/%5B,,%5D", "_self");
  };

  const dashboardsFiltersRedirectProps = (filterPeriod: any, isLast: boolean) => {
    const isActiveFilter = selectedFilter === LAWSUIT_COUNTER_FILTERS.ACTIVE_LAWSUIT.queryParam;
    if (isActiveFilter && !isLast) return {};

    return { onClick: () => setLawsuitsCounterRedirect(filterPeriod) };
  };

  const header = {
    title: shouldRenderLawsuitCounterFilter
      ? "Processos e casos do escritório"
      : "Processos e casos ativos do escritório",
    description: `Confira a quantidade de processos e casos ativos${
      shouldRenderLawsuitCounterFilter ? ", cadastrados ou encerrados" : ""
    } nos últimos 3 meses para analisar o histórico trimestral do escritório.`,
  };

  const toggleTip = {
    title: shouldRenderLawsuitCounterFilter ? "Processos e casos" : "Processos e casos ativos",
    text: (
      <ToggletipTextWrapper>
        {shouldRenderLawsuitCounterFilter ? (
          <ToggletipText>
            No indicador, as quantidades de processos e casos são somadas.
          </ToggletipText>
        ) : (
          <ToggletipText>
            No indicador, são considerados os processos e casos que ainda não foram encerrados no
            Astrea, portanto estão ativos.
          </ToggletipText>
        )}

        <ToggletipText>
          Compare a situação do mês atual com o fechamento dos últimos dois meses.
        </ToggletipText>
      </ToggletipTextWrapper>
    ),
    onClickCallback: () => {
      sendEventChartUsed(
        DASHBOARD_CHART_TYPES.LAWSUIT_COUNTER,
        dashboardChartFilterAndActionTypes.TOGGLE_TIP_OPENED
      );
    },
    alwaysOnLeft: shouldRenderTooltipAtLeft,
    callToAction: {
      text: "Acessar central de ajuda",
      callback: () => {
        sendEventChartUsed(
          DASHBOARD_CHART_TYPES.LAWSUIT_COUNTER,
          dashboardChartFilterAndActionTypes.HELP_CENTER_CLICK
        );
        window.open(
          "https://astrea.aurum.com.br/pt-BR/articles/8043378-como-analisar-os-processos-e-casos-do-escritorio",
          "_blank"
        );
      },
    },
  };

  return (
    <DashboardChartWrapper
      title={header.title}
      description={header.description}
      toggleTip={toggleTip}
    >
      <LawsuitCounterContainer>
        {shouldRenderLawsuitCounterFilter && (
          <DashboardChartFilters>
            <DashboardChartLawsuitCountTypeFilter
              // @ts-ignore
              selectedCountType={selectedFilter}
              setCountTypeFunction={setLawsuitCounterFilterAndSendMixPanelEvent}
            />
          </DashboardChartFilters>
        )}
        <QueryLoaderWrapper
          // @ts-ignore
          queryStatus={queryStatus}
          spinnerConfig={{ height: 137.6, isCounter: true }}
          skeletonComponent={<Skeletons.LawsuitCounterSkeleton />}
        >
          <CounterContainer>
            {/* @ts-ignore */}
            {counterValues?.map(
              (
                period: { name: string; value: number; previousValue: number; filterPeriod: {} },
                index: number
              ) => {
                const isLast = index === counterValues?.length - 1;

                return (
                  <Stat
                    key={period.name}
                    label={period.name}
                    value={period.value}
                    previousValue={period.previousValue}
                    comparisonDetails="comparado ao mês anterior"
                    shouldRenderComparison={shouldRenderComparison}
                    {...{ ...dashboardsFiltersRedirectProps(period.filterPeriod, isLast) }}
                  />
                );
              }
            )}
          </CounterContainer>
        </QueryLoaderWrapper>
      </LawsuitCounterContainer>
    </DashboardChartWrapper>
  );
};

export default LawsuitCounter;
